import * as React from 'react';
import { Redirect, Route } from 'react-router-dom';

import { appURLs, getLandingPagePartialURL, pageTitle } from '@utils/AppUtil';
import {
  getUserProfile,
  hasCEExecutiveReportsAccess,
  hasCreateCasePageAccess,
  hasCRMExecutiveReportsAccess,
  hasProductAdminAccess,
  hasProductCEAccess,
  hasProductCRMAccess,
  hasReportAccess,
  hasSiteAdminAccess,
  hasUserAdminAccess,
  isLoggedIn,
  userLogOut,
} from '@utils/AuthUtil';
import { persistPostLoginRedirectUrl } from '@utils/PostLoginRedirectUtil';

let savedCaseDetailsURL = null;
let savedCSDetailsURL = null;

const redirectLoginPage = (rest: any) => {
  const { agencyId } = rest.computedMatch.params;
  const redirectUrl = agencyId ? `/agencies/${agencyId}/login` : '/login';

  return (
    <Redirect
      to={{
        pathname: redirectUrl,
        state: { from: rest.location },
      }}
    />
  );
};

const redirectAdminHomePage = (rest: any) => {
  const redirectUrl = '/';

  return (
    <Redirect
      to={{
        pathname: redirectUrl,
        state: { from: rest.location },
      }}
    />
  );
};

const redirectAgencyLandingPage = (rest: any) => {
  const { agencyId } = rest.computedMatch.params;
  const userProfile = getUserProfile();
  const landingPage = getLandingPagePartialURL(userProfile);
  const redirectUrl = `/agencies/${agencyId}/${landingPage}`;

  return (
    <Redirect
      to={{
        pathname: redirectUrl,
        state: { from: rest.location },
      }}
    />
  );
};

const redirectErrorPage = (rest: any) => {
  const { agencyId } = rest.computedMatch.params;
  const redirectUrl = `/agencies/${agencyId}/error`;

  /**
   * clearing saved case details URL to restrict
   * further redirection to case details
   */
  savedCaseDetailsURL = null;
  savedCSDetailsURL = null;

  return (
    <Redirect
      to={{
        pathname: redirectUrl,
        state: { from: rest.location },
      }}
    />
  );
};

const redirectCaseDetailsPage = (rest: any) => {
  const redirectUrl = savedCaseDetailsURL;

  /**
   * clearing saved case details URL to restrict
   * further redirection to case details
   */
  savedCaseDetailsURL = null;

  return (
    <Redirect
      to={{
        pathname: redirectUrl,
        state: { from: rest.location },
      }}
    />
  );
};

const redirectCSDetailsPage = (rest: any) => {
  const redirectUrl = savedCSDetailsURL;

  /**
   * clearing saved case details URL to restrict
   * further redirection to case details
   */
  savedCSDetailsURL = null;

  return (
    <Redirect
      to={{
        pathname: redirectUrl,
        state: { from: rest.location },
      }}
    />
  );
};

const isAuthorizedAgency = (rest: any, userProfile: IUserProfile): boolean =>
  userProfile.agencyId === parseInt(rest.computedMatch.params.agencyId, 10);

const PrivateRoute = ({ component: Component, ...rest }) => {
  const userProfile = getUserProfile();
  const { agencyId } = rest.computedMatch.params;
  const isAgencyApp = !!agencyId;

  const redirectURL = rest.location.pathname;
  const { caseCreatePartialURL, faqManagementPartialURL } = appURLs;

  if (!isLoggedIn()) {
    persistPostLoginRedirectUrl(rest.location);
    const createCaseURL = `agencies/${agencyId}/${caseCreatePartialURL}`;
    const caseDetailsURL = `agencies/${agencyId}/${appURLs.casePartialURL}`;
    const customerSubmissionURL = `agencies/${agencyId}/${appURLs.customerSubmissionPartialURL}`;
    const contactsPartialURL = `agencies/${agencyId}/${appURLs.contactsPartialURL}`;
    switch (true) {
      case redirectURL.includes(createCaseURL):
        rest.title = pageTitle.createCase;
        savedCaseDetailsURL = redirectURL;
        break;
      case redirectURL.includes(caseDetailsURL):
        rest.title = pageTitle.caseDetailPage;
        savedCaseDetailsURL = redirectURL;
        break;
      case redirectURL.includes(customerSubmissionURL):
        rest.title = pageTitle.customerSubmissionDetailPage;
        savedCSDetailsURL = redirectURL;
        break;
      case redirectURL.includes(contactsPartialURL):
        rest.title = pageTitle.contactsPage;

        break;
    }

    userLogOut();

    return redirectLoginPage(rest);
  }

  const hasNoAccess =
    !hasProductCEAccess(userProfile) &&
    !hasProductCRMAccess(userProfile) &&
    !hasProductAdminAccess(ConfigProductType.XERCE, userProfile) &&
    !hasProductAdminAccess(ConfigProductType.CRM, userProfile) &&
    !hasSiteAdminAccess(userProfile) &&
    !hasUserAdminAccess(userProfile);

  const isUserOnAuthorizedAgency = isAgencyApp && isAuthorizedAgency(rest, userProfile);

  if (isAgencyApp && (hasNoAccess || !isUserOnAuthorizedAgency)) {
    userLogOut();

    return redirectLoginPage(rest);
  }

  if (savedCaseDetailsURL !== null && hasProductCEAccess(userProfile)) {
    return redirectCaseDetailsPage(rest);
  }

  if (savedCaseDetailsURL !== null && !hasProductCEAccess(userProfile)) {
    return redirectErrorPage(rest);
  }

  if (savedCSDetailsURL !== null && hasProductCRMAccess(userProfile)) {
    return redirectCSDetailsPage(rest);
  }

  if (savedCSDetailsURL !== null && !hasProductCRMAccess(userProfile)) {
    return redirectErrorPage(rest);
  }

  if (redirectURL.includes(caseCreatePartialURL) && !hasCreateCasePageAccess(userProfile)) {
    return redirectErrorPage(rest);
  }

  if (
    redirectURL.includes(faqManagementPartialURL) &&
    !(userProfile.scopes.crm && userProfile.scopes.crm.isFAQEnabled)
  ) {
    return redirectErrorPage(rest);
  }

  if (
    redirectURL.includes(appURLs.reportsPartialURL) &&
    !hasReportAccess(userProfile) &&
    !hasCEExecutiveReportsAccess(userProfile) &&
    !hasCRMExecutiveReportsAccess(userProfile)
  ) {
    return redirectErrorPage(rest);
  }

  if (redirectURL.includes(appURLs.customerSubmissionsPartialURL) && !hasProductCRMAccess(userProfile)) {
    return redirectErrorPage(rest);
  }

  if (rest.acl) {
    const acl: ACL = rest.acl;

    if (acl === ACL.COMCATE_ADMIN && !userProfile.scopes.comcateAdmin) {
      return redirectAdminHomePage(rest);
    }

    if (acl === ACL.SITE_ADMIN && !hasSiteAdminAccess(userProfile)) {
      return redirectAgencyLandingPage(rest);
    }

    if (acl === ACL.USER_ADMIN && !hasUserAdminAccess(userProfile)) {
      return redirectAgencyLandingPage(rest);
    }

    if (acl === ACL.PRODUCT_ADMIN_XERCE && !hasProductAdminAccess(ConfigProductType.XERCE, userProfile)) {
      return redirectAgencyLandingPage(rest);
    }

    if (acl === ACL.PRODUCT_ADMIN_CRM && !hasProductAdminAccess(ConfigProductType.CRM, userProfile)) {
      return redirectAgencyLandingPage(rest);
    }

    if (acl === ACL.CRM && !hasProductCRMAccess(userProfile)) {
      return redirectAgencyLandingPage(rest);
    }

    if (acl === ACL.AUDIT_TRAIL && !userProfile.hasAuditTrailAccess) {
      return redirectAgencyLandingPage(rest);
    }
  }

  if (rest.title) {
    document.title = `${userProfile.agencyName}: ${rest.title}`;
  }

  return <Route {...rest} render={(props) => <Component {...props} />} />;
};

export default PrivateRoute;
